export interface Flag {
  rule: string;
  message: string;
}

export interface Stay {
  checkIn: string;
  checkOut: string;
  nights: string[];
  source: 'set' | 'default';
}

export interface Room {
  none?: boolean;
  checkIn?: string;
  checkOut?: string;
  roomType?: string;
  guest?: string;
  sharesWith?: string;
  assistantEmail?: string;
  notes?: string;
  confirmed?: boolean;
  confirmation?: string;
  hotel?: 'alternate';
}

export interface HotelListEntry {
  present: boolean;
  checkIn: string | null;
  checkOut: string | null;
  nameOnList: string | null;
  nameDiffers?: boolean;
  confirmation: string | null;
  asOf: string;
}

export interface Night {
  date: string;
  label: string;
  rooms: number;
  inBlock: boolean;
  left: number | null;
  over: boolean;
}

export interface PerNight {
  nights: Night[];
  blockSize: number | null;
  peak: number;
  peakNight: string | null;
  roomsLeft: number | null;
  outsideBlock: number;
  roomNights: number;
}

export interface ChangeRow {
  key: string;
  name: string;
  changes?: { field: string; from: unknown; to: unknown }[];
}
export interface Changes {
  added: ChangeRow[];
  changed: ChangeRow[];
  removed: ChangeRow[];
  unchanged: number;
  sentBefore: boolean;
}

export interface HotelPerson {
  inviteeId: string;
  name: string;
  email: string | null;
  role: string | null;
  audienceKey: string | null;
  status: string;
  expected: boolean;
  hotelRequested: boolean;
  stay: Stay | null;
  room: Room;
  flags: Flag[];
  sent: 'yes' | 'changed' | 'no';
  hotelList: HotelListEntry | null;
  onList: 'yes' | 'dates_differ' | 'missing' | 'not_expected' | null;
}

export interface ReturnedRow {
  key: string | null;
  name: string | null;
  nameOnList: string | null;
  nameDiffers: boolean;
  status: 'ok' | 'dates_differ' | 'missing_in_hotel' | 'missing_in_platform' | 'not_expected';
  platform: { checkIn: string; checkOut: string } | null;
  hotel: { checkIn: string | null; checkOut: string | null; confirmation?: string | null } | null;
}

export interface ReturnedSummary {
  onList: number;
  ok: number;
  datesDiffer: number;
  missingInHotel: number;
  missingInPlatform: number;
  notExpected: number;
  nameDiffers: number;
  unreadDates: number;
}

export interface IncreaseRequest {
  rooms: number;
  requestedOn: string | null;
  answer: 'pending' | 'granted' | 'partly' | 'declined';
  answeredOn: string | null;
  note: string | null;
  grantedRooms?: number;
}

export interface HotelView {
  hotel: {
    name: string | null;
    blockSize: number | null;
    blockNights: string[];
    contactEmail: string | null;
    checkOutDay: string | null;
    incidentals: 'guest' | 'master';
    increaseRequests: IncreaseRequest[];
    alternate: { name: string; reason: string | null; contactEmail: string | null } | null;
  };
  perNight: PerNight;
  alternatePerNight: PerNight | null;
  counts: {
    requested: number;
    withRoom: number;
    rooms: number;
    flagged: number;
    datesSet: number;
    outsideBlock: number;
    alternate: number;
  };
  lastSent: { sentAt: string; rows: number } | null;
  changeCounts: { added: number; changed: number; removed: number };
  peopleVisible: boolean;
  people?: HotelPerson[];
  changes?: Changes;
  returned: {
    at: string;
    fileName?: string;
    summary: ReturnedSummary;
    hotelOnly?: {
      key: string;
      nameOnList: string;
      checkIn: string | null;
      checkOut: string | null;
      confirmation: string | null;
    }[];
  } | null;
  sharers?: { inviteeId: string; name: string }[];
}

export interface MealCount {
  key: string;
  label: string;
  count: number;
  platform?: number;
  extra?: { count: number; note: string | null } | null;
  partnerGuests?: number;
  audiences?: string[] | null;
}
export interface DietGroup {
  tag: string;
  count: number;
}
export interface DayPlan {
  date: string;
  label: string | null;
  /** The dinner the evening before the first day. */
  eve?: boolean;
  attendees: number;
  meals: MealCount[];
  dietary: DietGroup[];
  withDietary: number;
  dinner: { count: number; declined: number; choices: { choice: string; count: number }[]; noChoice: number } | null;
  kosher?: { count: number; noticeBy: string };
  dinnerChoices?: string[];
}
export interface Dinner {
  attending?: boolean;
  choice?: string;
  request?: string;
}
export interface CateringPerson {
  inviteeId: string;
  name: string;
  role: string | null;
  days: string[];
  dietary: string | null;
  tags: string[];
  dinners: Record<string, Dinner>;
  /** The dinners (dates) this person is counted at. */
  atDinners?: string[];
}

export interface RunSheetLine {
  date: string;
  time: string;
  what: string;
  room: string | null;
  leftovers: string | null;
}
export interface CateringSettings {
  mealAudiences: Record<string, Record<string, string[]>>;
  extra: Record<string, Record<string, { count: number; note: string | null }>>;
  dinnerChoices: Record<string, string[]>;
  runSheet: RunSheetLine[];
  eveDinner?: boolean;
}

export interface CateringView {
  days: { date: string; label: string | null; dinner: boolean; meals: string[]; eve?: boolean }[];
  meals: { key: string; label: string }[];
  caterer: string | null;
  settings: CateringSettings;
  audiences: { key: string; name: string }[];
  plan: DayPlan[];
  inPerson: number;
  lastSent: { sentAt: string } | null;
  changeCounts: { added: number; changed: number; removed: number };
  peopleVisible: boolean;
  people?: CateringPerson[];
  changes?: Changes;
}

export interface DayDietaryView {
  date: string;
  label: string | null;
  attendees: number;
  meals: MealCount[];
  dietary: DietGroup[];
  withDietary: number;
  dinner: DayPlan['dinner'];
  peopleVisible: boolean;
  people?: {
    inviteeId: string;
    name: string;
    dietary: string | null;
    tags: string[];
    dinner: Dinner | null;
    atDinner?: boolean;
  }[];
}

export interface FilePayload {
  fileName: string;
  contentType: string;
  content: string;
  digest?: string;
}

export const monthDay = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-CA', { timeZone: 'UTC', month: 'short', day: 'numeric' });
export const weekDay = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-CA', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
export const when = (at: string) =>
  new Date(at).toLocaleString('en-CA', { timeZone: 'America/Toronto', dateStyle: 'medium', timeStyle: 'short' });

export const toBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export function downloadFile(file: FilePayload) {
  const bytes = Uint8Array.from(atob(file.content), (ch) => ch.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: file.contentType }));
  const a = document.createElement('a');
  a.href = url;
  a.download = file.fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/** Table classes (docs/redesign/02-components.md, Tables): quiet 13px header over a 1px rule, 15px cells, rows
 *  at least 44px tall with a hairline between them. Right-align numbers with `num`. */
export const th = 'py-2 pr-3 last:pr-0 align-bottom font-semibold';
export const td = 'py-[11px] pr-3 last:pr-0 align-top text-[15px] leading-[22px] text-ink';
export const headRow = 'text-left text-small text-ink-2 border-b border-line-strong';
export const num = 'text-right tabular';
/** Secondary text inside a cell, on its own line. */
export const em = 'block text-small text-ink-3';
